/**
 * AIShield Customer Website Onboarding Wizard (UI Batch 2 Redesign)
 *
 * Compact modal shell (640–680px), softly rounded charcoal surfaces,
 * prominent blue actions, readable sentence-case typography, and truthful
 * step progression across Details, Ownership, DNS Routing, and Status.
 *
 * Invariants preserved:
 * - Shared lifecycle manager coordinates abort signals and delayed transitions.
 * - Stale responses guarded against in all async callbacks.
 * - CNAME instructions loaded strictly when modal is open.
 * - Focus trapped inside modal, restored to trigger element on close.
 * - Escape key dismisses modal and cancels pending requests.
 * - Truthful backend-driven status derivation (deriveSetupStatus).
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "motion/react";
import {
  X,
  Globe,
  Check,
  Copy,
  AlertCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  ShieldCheck,
  Clock,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useAuth } from "../../hooks/useAuth.tsx";
import {
  createWebsite,
  fetchWebsite,
  verifyWebsiteTxt,
  fetchCnameInstructions,
  verifyWebsiteCname,
  ApiClientError,
} from "../../services/apiClient.ts";
import type {
  WebsiteRecord,
  CnameInstructions,
} from "../../types/telemetry.ts";
import { formatDateTimeIST } from "../../utils/dateTime.ts";
import {
  type OnboardingStep,
  determineInitialStep,
  deriveSetupStatus,
  WizardLifecycleManager,
} from "./wizardLogic.ts";

export { determineInitialStep, type OnboardingStep, WizardLifecycleManager } from "./wizardLogic.ts";

interface OnboardingWizardProps {
  isOpen: boolean;
  initialWebsite?: WebsiteRecord | null;
  fixtureMode?: string | null;
  onClose: () => void;
  onWebsiteCreated: (website: WebsiteRecord) => void;
  onWebsiteUpdated: (website: WebsiteRecord) => void;
  onFinish: (website: WebsiteRecord) => void;
}

const STEP_ORDER: OnboardingStep[] = ["DETAILS", "VERIFY_TXT", "CONNECT_DNS", "SETUP_STATUS"];

const STEP_SHORT_LABELS: Record<OnboardingStep, string> = {
  DETAILS: "Details",
  VERIFY_TXT: "Ownership",
  CONNECT_DNS: "DNS routing",
  SETUP_STATUS: "Status",
};

/**
 * Accessible Copy Button with visible success feedback
 */
const CopyButton: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard fallback
      const textArea = document.createElement("textarea");
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), 2000);
      } catch {
        // ignore
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      data-testid={`copy-button-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[8px] bg-panel-subtle hover:bg-panel border border-border text-foreground-muted hover:text-foreground transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-400 font-medium">Copied!</span>
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" />
          <span>Copy</span>
        </>
      )}
    </button>
  );
};

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  isOpen,
  initialWebsite,
  fixtureMode,
  onClose,
  onWebsiteCreated,
  onWebsiteUpdated,
  onFinish,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const { account, logout } = useAuth();

  // Modal and focus tracking refs
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Active website and step state
  const [website, setWebsite] = useState<WebsiteRecord | null>(initialWebsite || null);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(() =>
    determineInitialStep(initialWebsite)
  );

  // Refreshing record on resume state
  const [isRefreshingRecord, setIsRefreshingRecord] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // Coordinated lifecycle manager for in-flight requests and delayed step transitions
  const lifecycleManagerRef = useRef<WizardLifecycleManager | null>(null);
  if (!lifecycleManagerRef.current) {
    lifecycleManagerRef.current = new WizardLifecycleManager((step) => {
      setCurrentStep(step);
    });
  }

  // Step 1 State: Website Details
  const [displayName, setDisplayName] = useState("");
  const [publicHostname, setPublicHostname] = useState("");
  const [originUrl, setOriginUrl] = useState("");
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isSubmittingDetails, setIsSubmittingDetails] = useState(false);

  // Step 2 State: Ownership Verification
  const [txtVerificationState, setTxtVerificationState] = useState<{
    status:
      | "idle"
      | "verifying"
      | "verified"
      | "mismatch"
      | "pending"
      | "timeout"
      | "overload"
      | "rate_limited"
      | "error";
    message?: string;
  }>({ status: "idle" });
  const [retryAfterCooldown, setRetryAfterCooldown] = useState<number>(0);
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step 3 State: DNS CNAME Routing
  const [cnameInstructions, setCnameInstructions] = useState<CnameInstructions | null>(null);
  const [cnameLoading, setCnameLoading] = useState(false);
  const [cnameError, setCnameError] = useState<string | null>(null);
  const [cnameVerificationState, setCnameVerificationState] = useState<{
    status: "idle" | "verifying" | "ready" | "mismatch" | "pending" | "rate_limited" | "error";
    message?: string;
  }>({ status: "idle" });
  const [cnameCooldown, setCnameCooldown] = useState<number>(0);

  /**
   * Cleans up all pending asynchronous work, transitions, and intervals
   */
  const cancelPendingWork = useCallback(() => {
    lifecycleManagerRef.current?.cancelPendingWork();
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = null;
    }
  }, []);

  // Cleanup pending work on unmount
  useEffect(() => {
    return () => {
      cancelPendingWork();
    };
  }, [cancelPendingWork]);

  // Cancel pending requests when wizard closes, selected website changes, or account changes
  useEffect(() => {
    if (!isOpen) {
      cancelPendingWork();
    }
  }, [isOpen, cancelPendingWork]);

  useEffect(() => {
    cancelPendingWork();
  }, [initialWebsite?.id, account?.id, cancelPendingWork]);

  // Handle countdown interval for TXT rate limiting
  useEffect(() => {
    if (retryAfterCooldown > 0) {
      const timer = setInterval(() => {
        setRetryAfterCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [retryAfterCooldown]);

  // Handle countdown interval for CNAME rate limiting
  useEffect(() => {
    if (cnameCooldown > 0) {
      const timer = setInterval(() => {
        setCnameCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cnameCooldown]);

  // Reset state and refresh from backend when opening or changing initial website
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Reset transient interaction states
    setDetailsError(null);
    setIsSubmittingDetails(false);
    setTxtVerificationState({ status: "idle" });
    setRetryAfterCooldown(0);
    setCnameInstructions(null);
    setCnameLoading(false);
    setCnameError(null);
    setCnameVerificationState({ status: "idle" });
    setCnameCooldown(0);
    setRefreshError(null);

    if (!initialWebsite) {
      // Fresh new website attempt
      setWebsite(null);
      setCurrentStep("DETAILS");
      setDisplayName("");
      setPublicHostname("");
      setOriginUrl("");
      setIsRefreshingRecord(false);
      return;
    }

    // Resuming an existing website
    const siteId = initialWebsite.id;

    if (fixtureMode) {
      // In fixture mode, use provided fixture record directly
      setWebsite(initialWebsite);
      setCurrentStep(determineInitialStep(initialWebsite));
      setIsRefreshingRecord(false);
      return;
    }

    // Live mode: refresh record from backend to resume from truthful current state
    const signal = lifecycleManagerRef.current!.createSignal();
    setIsRefreshingRecord(true);

    fetchWebsite(siteId, { signal })
      .then((freshSite) => {
        if (signal.aborted) return;
        setWebsite(freshSite);
        onWebsiteUpdated(freshSite);
        setCurrentStep(determineInitialStep(freshSite));
      })
      .catch(async (err: unknown) => {
        if (signal.aborted) return;
        if (err instanceof ApiClientError) {
          if (err.status === 401) {
            await logout();
            return;
          }
          if (err.status === 404) {
            setWebsite(null);
            setRefreshError("Website not found or no longer accessible.");
            return;
          }
        }
        const msg = err instanceof Error ? err.message : "Failed to load website configuration.";
        setRefreshError(msg);
        setWebsite(initialWebsite);
        setCurrentStep(determineInitialStep(initialWebsite));
      })
      .finally(() => {
        if (!signal.aborted) {
          setIsRefreshingRecord(false);
        }
      });

    return () => {
      lifecycleManagerRef.current?.cancelPendingWork();
    };
  }, [isOpen, initialWebsite, fixtureMode, logout, onWebsiteUpdated]);

  // Load CNAME instructions when entering Step 3 (strictly when modal is OPEN)
  const loadCnameInstructions = useCallback(
    async (siteId: string) => {
      if (!isOpen) return;

      if (fixtureMode === "onboarding-step3-unconfigured") {
        setCnameInstructions({
          record_type: "CNAME",
          record_name: "app.customer.com",
          expected_target: null,
          status: "INGRESS_NOT_CONFIGURED",
          actionable: false,
          description: "Ingress routing target is not configured.",
          message:
            "Ingress not configured. Administrator has not configured an ingress CNAME target. Actionable routing instructions are withheld until ingress is configured.",
        });
        return;
      }

      if (fixtureMode) {
        setCnameInstructions({
          record_type: "CNAME",
          record_name: website?.public_hostname || "app.customer.com",
          expected_target: "ingress.aishield.internal",
          status: "READY",
          actionable: true,
          description: `Create a DNS CNAME record pointing '${website?.public_hostname || "app.customer.com"}' to 'ingress.aishield.internal'.`,
          message:
            "Add a CNAME record at your authoritative DNS provider pointing your subdomain to ingress.aishield.internal.",
        });
        return;
      }

      const signal = lifecycleManagerRef.current!.createSignal();

      setCnameLoading(true);
      setCnameError(null);

      try {
        const instructions = await fetchCnameInstructions(siteId, { signal });
        if (signal.aborted || !isOpen) return;
        setCnameInstructions(instructions);
      } catch (err: unknown) {
        if (signal.aborted || !isOpen) return;
        if (err instanceof ApiClientError && err.status === 401) {
          await logout();
          return;
        }
        const msg = err instanceof Error ? err.message : "Failed to load DNS CNAME instructions";
        setCnameError(msg);
      } finally {
        if (!signal.aborted) {
          setCnameLoading(false);
        }
      }
    },
    [isOpen, fixtureMode, website, logout]
  );

  useEffect(() => {
    if (isOpen && currentStep === "CONNECT_DNS" && website?.id) {
      loadCnameInstructions(website.id);
    }
  }, [isOpen, currentStep, website?.id, loadCnameInstructions]);

  // Focus management: capture trigger, move focus into modal, restore on close
  useEffect(() => {
    if (isOpen) {
      triggerElementRef.current = document.activeElement as HTMLElement | null;
      const timer = setTimeout(() => {
        if (!modalRef.current) return;
        const focusable = modalRef.current.querySelector<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable) {
          focusable.focus();
        } else {
          modalRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    } else {
      if (triggerElementRef.current && typeof triggerElementRef.current.focus === "function") {
        triggerElementRef.current.focus();
        triggerElementRef.current = null;
      }
    }
  }, [isOpen]);

  // Tab containment (focus trap) and Escape handling
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === "Tab" && modalRef.current) {
        const focusables = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => el.offsetParent !== null);

        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first || !modalRef.current.contains(document.activeElement)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last || !modalRef.current.contains(document.activeElement)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // --- Step 1: Submit Website Details ---
  const handleSubmitDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingDetails) return;

    setDetailsError(null);

    const trimmedName = displayName.trim();
    const trimmedHost = publicHostname.trim().toLowerCase();
    const trimmedOrigin = originUrl.trim();

    if (!trimmedName) {
      setDetailsError("Website name is required.");
      return;
    }
    if (!trimmedHost) {
      setDetailsError("Public website hostname is required.");
      return;
    }
    if (trimmedHost.includes("://") || trimmedHost.includes("/")) {
      setDetailsError("Hostname must not include a protocol (http://) or path.");
      return;
    }
    if (!trimmedOrigin) {
      setDetailsError("Origin URL is required.");
      return;
    }
    if (!/^https?:\/\//i.test(trimmedOrigin)) {
      setDetailsError("Origin URL must start with http:// or https://");
      return;
    }

    const signal = lifecycleManagerRef.current!.createSignal();

    setIsSubmittingDetails(true);

    try {
      if (fixtureMode) {
        const mockSite: WebsiteRecord = {
          id: `fixture-${Date.now()}`,
          account_id: "00000000-0000-0000-0000-000000000000",
          display_name: trimmedName,
          public_hostname: trimmedHost,
          origin_url: trimmedOrigin,
          origin_host_header: null,
          onboarding_status: "DRAFT",
          policy_mode: "BLOCKING",
          ai_mode: "SHADOW",
          tls_status: "NOT_PROVISIONED",
          is_development: false,
          verification_token: "tok_fixture_abc123",
          dns_txt_record_name: `_aishield-challenge.${trimmedHost}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          verified_at: null,
          activated_at: null,
          dns_instructions: {
            record_type: "TXT",
            record_name: `_aishield-challenge.${trimmedHost}`,
            expected_value: "aishield-verify=tok_fixture_abc123",
            description: `Create a DNS TXT record at '_aishield-challenge.${trimmedHost}' with value 'aishield-verify=tok_fixture_abc123'.`,
          },
        };
        if (signal.aborted || !isOpen) return;
        setWebsite(mockSite);
        onWebsiteCreated(mockSite);
        setCurrentStep("VERIFY_TXT");
        return;
      }

      const newSite = await createWebsite(
        {
          display_name: trimmedName,
          public_hostname: trimmedHost,
          origin_url: trimmedOrigin,
        },
        { signal }
      );

      if (signal.aborted || !isOpen) return;
      setWebsite(newSite);
      onWebsiteCreated(newSite);
      setCurrentStep("VERIFY_TXT");
    } catch (err: unknown) {
      if (signal.aborted || !isOpen) return;
      if (err instanceof ApiClientError && err.status === 401) {
        await logout();
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to create website";
      setDetailsError(msg);
    } finally {
      if (!signal.aborted) {
        setIsSubmittingDetails(false);
      }
    }
  };

  // --- Step 2: Check TXT Ownership Verification ---
  const handleCheckTxtVerification = async () => {
    if (!website || retryAfterCooldown > 0 || txtVerificationState.status === "verifying") return;

    const signal = lifecycleManagerRef.current!.createSignal();

    setTxtVerificationState({ status: "verifying" });

    try {
      if (fixtureMode) {
        const updatedSite: WebsiteRecord = {
          ...website,
          onboarding_status: "OWNERSHIP_VERIFIED",
          verified_at: new Date().toISOString(),
          verification_status: "VERIFIED",
          verification_message: "DNS TXT ownership challenge verified successfully.",
        };
        if (signal.aborted || !isOpen) return;
        setWebsite(updatedSite);
        onWebsiteUpdated(updatedSite);
        setTxtVerificationState({
          status: "verified",
          message: "Domain ownership verified successfully!",
        });
        lifecycleManagerRef.current!.scheduleStepTransition("CONNECT_DNS", 1200);
        return;
      }

      const res = await verifyWebsiteTxt(website.id, { signal });
      if (signal.aborted || !isOpen) return;

      setWebsite(res);
      onWebsiteUpdated(res);

      if (res.onboarding_status === "OWNERSHIP_VERIFIED" || res.verification_status === "ALREADY_VERIFIED") {
        setTxtVerificationState({
          status: "verified",
          message: res.verification_message || "Domain ownership verified successfully!",
        });
        lifecycleManagerRef.current!.scheduleStepTransition("CONNECT_DNS", 1200);
      } else if (res.verification_status === "MISMATCH") {
        setTxtVerificationState({
          status: "mismatch",
          message: res.verification_message || "TXT record found, but the value does not match the expected challenge.",
        });
      } else if (res.verification_status === "TIMEOUT") {
        setTxtVerificationState({
          status: "timeout",
          message: res.verification_message || "DNS lookup timed out while resolving TXT record.",
        });
      } else {
        setTxtVerificationState({
          status: "pending",
          message: res.verification_message || "TXT record not found yet. DNS changes may take a few moments to propagate.",
        });
      }
    } catch (err: unknown) {
      if (signal.aborted || !isOpen) return;
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          await logout();
          return;
        }
        if (err.status === 429) {
          const cooldown = err.retryAfter ?? 10;
          setRetryAfterCooldown(cooldown);
          setTxtVerificationState({
            status: "rate_limited",
            message: `Rate limit reached. Please wait ${cooldown} seconds before checking again.`,
          });
          return;
        }
        if (err.status === 503) {
          const cooldown = err.retryAfter ?? 15;
          setRetryAfterCooldown(cooldown);
          setTxtVerificationState({
            status: "overload",
            message: err.message || "DNS verification service overloaded. Please wait before retrying.",
          });
          return;
        }
        if (err.status === 504) {
          setTxtVerificationState({
            status: "timeout",
            message: "DNS resolution timed out. Please try again.",
          });
          return;
        }
      }
      const msg = err instanceof Error ? err.message : "Verification check failed";
      setTxtVerificationState({ status: "error", message: msg });
    }
  };

  // --- Step 3: Check CNAME Routing Readiness ---
  const handleVerifyCname = async () => {
    if (!website || cnameCooldown > 0 || cnameVerificationState.status === "verifying") return;

    const signal = lifecycleManagerRef.current!.createSignal();

    setCnameVerificationState({ status: "verifying" });

    try {
      if (fixtureMode) {
        const updatedSite: WebsiteRecord = {
          ...website,
          onboarding_status: "ROUTING_READY",
        };
        if (signal.aborted || !isOpen) return;
        setWebsite(updatedSite);
        onWebsiteUpdated(updatedSite);
        setCnameVerificationState({
          status: "ready",
          message: "CNAME routing verified! Hostname resolves to AIShield ingress target.",
        });
        lifecycleManagerRef.current!.scheduleStepTransition("SETUP_STATUS", 1200);
        return;
      }

      const res = await verifyWebsiteCname(website.id, { signal });
      if (signal.aborted || !isOpen) return;

      setWebsite(res);
      onWebsiteUpdated(res);

      if (res.onboarding_status === "ROUTING_READY") {
        setCnameVerificationState({
          status: "ready",
          message: res.verification_message || "DNS CNAME routing is verified and ready!",
        });
        lifecycleManagerRef.current!.scheduleStepTransition("SETUP_STATUS", 1200);
      } else if (res.verification_status === "MISMATCH") {
        setCnameVerificationState({
          status: "mismatch",
          message: res.verification_message || "CNAME record found, but target does not match configured ingress target.",
        });
      } else {
        setCnameVerificationState({
          status: "pending",
          message: res.verification_message || "CNAME record not found or not pointing to ingress target yet.",
        });
      }
    } catch (err: unknown) {
      if (signal.aborted || !isOpen) return;
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          await logout();
          return;
        }
        if (err.status === 429) {
          const cd = err.retryAfter ?? 10;
          setCnameCooldown(cd);
          setCnameVerificationState({
            status: "rate_limited",
            message: `Rate limit reached. Please wait ${cd} seconds before retrying.`,
          });
          return;
        }
      }
      const msg = err instanceof Error ? err.message : "CNAME verification failed";
      setCnameVerificationState({ status: "error", message: msg });
    }
  };

  if (!isOpen) return null;

  const txtRecordName =
    website?.dns_txt_record_name ||
    website?.dns_instructions?.record_name ||
    (website ? `_aishield-challenge.${website.public_hostname}` : "");

  const txtRecordValue =
    website?.dns_instructions?.expected_value ||
    (website?.verification_token ? `aishield-verify=${website.verification_token}` : "");

  const currentStepIndex = STEP_ORDER.indexOf(currentStep);
  const statusSummary = website ? deriveSetupStatus(website) : null;

  const isIngressConfigured = Boolean(
    cnameInstructions &&
      cnameInstructions.actionable &&
      cnameInstructions.status !== "INGRESS_NOT_CONFIGURED" &&
      cnameInstructions.expected_target
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
    >
      {/* Backdrop */}
      <motion.div
        initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-xs"
        aria-hidden="true"
      />

      {/* Modal Shell Card */}
      <motion.div
        ref={modalRef}
        tabIndex={-1}
        initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.15 }}
        className="relative w-full max-w-[660px] bg-panel border border-border/80 rounded-[22px] shadow-2xl text-foreground font-sans overflow-hidden z-10 my-auto flex flex-col max-h-[90vh] focus:outline-none"
      >
        {/* Top Header */}
        <div className="px-5 sm:px-6 py-4 border-b border-border/80 flex items-center justify-between shrink-0 bg-panel">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[10px] bg-blue-600/10 text-blue-400 border border-blue-500/20 flex items-center justify-center shrink-0">
              <Globe className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 id="onboarding-modal-title" className="text-base font-semibold text-foreground">
                  Add website
                </h2>
                {(fixtureMode || (website && website.is_development)) && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">
                    DEV FIXTURE
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close onboarding wizard"
            className="w-9 h-9 rounded-[10px] flex items-center justify-center text-foreground-muted hover:text-foreground hover:bg-panel-subtle border border-transparent hover:border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>

        {/* Compact Stepper / Progress Indicator */}
        <div className="px-5 sm:px-6 py-3 border-b border-border/60 bg-panel-subtle/30 shrink-0 select-none">
          <nav aria-label="Onboarding Progress" className="flex items-center justify-between">
            {STEP_ORDER.map((step, idx) => {
              const isPast = idx < currentStepIndex;
              const isCurrent = idx === currentStepIndex;
              const label = STEP_SHORT_LABELS[step];

              return (
                <React.Fragment key={step}>
                  <div
                    className="flex items-center gap-2 min-w-0"
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 transition-colors ${
                        isCurrent
                          ? "bg-blue-600 text-white font-semibold shadow-xs"
                          : isPast
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-medium"
                          : "bg-panel-subtle text-foreground-muted/60 border border-border text-xs"
                      }`}
                    >
                      {isPast ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                    </span>
                    <span
                      className={`text-xs transition-colors truncate ${
                        isCurrent
                          ? "font-semibold text-foreground"
                          : isPast
                          ? "font-medium text-foreground-muted hidden sm:inline"
                          : "font-medium text-foreground-muted/60 hidden sm:inline"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {idx < STEP_ORDER.length - 1 && (
                    <ChevronRight className="w-3.5 h-3.5 text-foreground-muted/30 mx-1 shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        </div>

        {/* Scrollable Modal Body */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 min-h-0 space-y-4">
          {/* Refreshing state overlay indicator */}
          {isRefreshingRecord && (
            <div className="p-3 rounded-[12px] bg-brand-subtle text-brand border border-brand-border flex items-center gap-2.5 text-xs font-sans">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <span>Checking latest website configuration...</span>
            </div>
          )}

          {refreshError && (
            <div
              role="alert"
              className="p-3.5 rounded-[12px] bg-verdict-block/10 border border-verdict-block/30 text-verdict-block flex items-start gap-2.5 text-xs"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="leading-relaxed">{refreshError}</div>
            </div>
          )}

          {/* ================= STEP 1: WEBSITE DETAILS ================= */}
          {currentStep === "DETAILS" && (
            <form
              id="step-details-form"
              onSubmit={handleSubmitDetails}
              className="space-y-4"
              data-testid="step-details-form"
            >
              <div>
                <h3 className="text-lg font-semibold text-foreground">Connect your website.</h3>
                <p className="text-sm text-foreground-muted mt-0.5 leading-relaxed">
                  Tell us which website to protect and where to forward allowed traffic.
                </p>
              </div>

              {detailsError && (
                <div
                  role="alert"
                  className="p-3 rounded-[12px] bg-verdict-block/10 border border-verdict-block/30 text-verdict-block flex items-start gap-2.5 text-xs"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="leading-relaxed">{detailsError}</div>
                </div>
              )}

              {/* Website Name */}
              <div className="space-y-1">
                <label
                  htmlFor="onboarding-display-name"
                  className="block text-sm font-medium text-foreground"
                >
                  Website name <span className="text-red-400">*</span>
                </label>
                <input
                  id="onboarding-display-name"
                  type="text"
                  required
                  maxLength={100}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Production API"
                  className="w-full h-12 px-3.5 text-sm font-sans rounded-[12px] bg-panel-subtle border border-border text-foreground placeholder:text-foreground-dim focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-colors"
                />
                <p className="text-xs text-foreground-dim">
                  A name you’ll recognize in your dashboard.
                </p>
              </div>

              {/* Public Hostname */}
              <div className="space-y-1">
                <label
                  htmlFor="onboarding-hostname"
                  className="block text-sm font-medium text-foreground"
                >
                  Public hostname <span className="text-red-400">*</span>
                </label>
                <input
                  id="onboarding-hostname"
                  type="text"
                  required
                  maxLength={253}
                  value={publicHostname}
                  onChange={(e) => setPublicHostname(e.target.value)}
                  placeholder="e.g. app.example.com"
                  className="w-full h-12 px-3.5 text-sm font-mono rounded-[12px] bg-panel-subtle border border-border text-foreground placeholder:text-foreground-dim focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-colors"
                />
                <p className="text-xs text-foreground-dim">
                  Use a subdomain, such as app.example.com.
                </p>

                {/* Subdomain restriction note with accessible Why? disclosure */}
                <div className="mt-1.5 p-2.5 rounded-[10px] bg-panel-subtle/80 border border-border/80 text-xs text-foreground-muted leading-relaxed">
                  <div>
                    <span className="font-medium text-foreground">Subdomains only for now.</span> Root domains such as <span className="font-mono text-amber-400">example.com</span> aren’t supported.
                  </div>
                  <details className="mt-1 text-foreground-dim">
                    <summary className="cursor-pointer hover:text-foreground-muted select-none text-[11px] font-medium inline-flex items-center gap-1">
                      Why?
                    </summary>
                    <p className="mt-1 pl-2 border-l border-border/80 text-[11px] text-foreground-dim leading-relaxed">
                      Standard DNS prohibits CNAME records at zone apexes (RFC 1912 §2.4).
                    </p>
                  </details>
                </div>
              </div>

              {/* Origin URL */}
              <div className="space-y-1">
                <label
                  htmlFor="onboarding-origin-url"
                  className="block text-sm font-medium text-foreground"
                >
                  Origin URL <span className="text-red-400">*</span>
                </label>
                <input
                  id="onboarding-origin-url"
                  type="url"
                  required
                  value={originUrl}
                  onChange={(e) => setOriginUrl(e.target.value)}
                  placeholder="https://origin.example.net"
                  className="w-full h-12 px-3.5 text-sm font-mono rounded-[12px] bg-panel-subtle border border-border text-foreground placeholder:text-foreground-dim focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-colors"
                />
                <p className="text-xs text-foreground-dim">
                  The server that currently serves your website.
                </p>
              </div>
            </form>
          )}

          {/* ================= STEP 2: VERIFY OWNERSHIP ================= */}
          {currentStep === "VERIFY_TXT" && website && (
            <div className="space-y-4" data-testid="step-ownership-container">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Verify you own this domain.</h3>
                <p className="text-sm text-foreground-muted mt-0.5 leading-relaxed">
                  Add this TXT record at your DNS provider, then check verification.
                </p>
              </div>

              {/* Challenge DNS Record Card */}
              <div className="bg-panel-subtle/40 border border-border/80 rounded-[14px] overflow-hidden">
                <div className="px-4 py-2.5 border-b border-border/70 bg-panel-subtle/80 flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground">Required DNS TXT record</span>
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 font-bold">
                    TXT
                  </span>
                </div>
                <div className="p-4 space-y-3 text-xs">
                  {/* Type */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-2.5 border-b border-border/50">
                    <span className="text-foreground-muted font-medium">Type</span>
                    <span className="font-mono font-bold text-blue-400 px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 self-start sm:self-auto">
                      TXT
                    </span>
                  </div>

                  {/* Name / Host */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2.5 border-b border-border/50">
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground-muted font-medium mb-1">Name / Host</div>
                      <div className="font-mono text-foreground font-medium break-all select-all p-2 rounded-lg bg-panel border border-border/60">
                        {txtRecordName}
                      </div>
                    </div>
                    <div className="sm:self-end">
                      <CopyButton value={txtRecordName} label="TXT record name" />
                    </div>
                  </div>

                  {/* Value */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground-muted font-medium mb-1">Value</div>
                      <div className="font-mono text-emerald-400 font-medium break-all select-all p-2 rounded-lg bg-panel border border-border/60">
                        {txtRecordValue}
                      </div>
                    </div>
                    <div className="sm:self-end">
                      <CopyButton value={txtRecordValue} label="TXT record value" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Numbered Configuration Instructions */}
              <div className="p-3.5 rounded-[12px] bg-panel-subtle/50 border border-border/70 text-xs text-foreground-muted space-y-1.5 leading-relaxed">
                <div className="font-medium text-foreground text-xs">Configuration instructions:</div>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Open your domain’s DNS settings.</li>
                  <li>Add the TXT record shown above.</li>
                  <li>Return here and check verification.</li>
                </ol>
                <div className="pt-1.5 border-t border-border/50 text-[11px] text-foreground-dim leading-relaxed space-y-0.5">
                  <p>
                    DNS changes propagate worldwide according to your provider's TTL. Propagation may take a few moments.
                  </p>
                  {website.verified_at && (
                    <p className="text-emerald-400">
                      Challenge verified on {formatDateTimeIST(website.verified_at)}.
                    </p>
                  )}
                </div>
              </div>

              {/* Verification Feedback Banner */}
              {txtVerificationState.status !== "idle" && (
                <div
                  role="status"
                  className={`p-3.5 rounded-[12px] text-xs flex items-start gap-2.5 leading-relaxed ${
                    txtVerificationState.status === "verified"
                      ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
                      : txtVerificationState.status === "verifying"
                      ? "bg-brand/10 border border-brand/30 text-brand"
                      : txtVerificationState.status === "rate_limited" || txtVerificationState.status === "pending"
                      ? "bg-amber-500/10 border border-amber-500/30 text-amber-300"
                      : "bg-verdict-block/10 border border-verdict-block/30 text-verdict-block"
                  }`}
                >
                  {txtVerificationState.status === "verifying" ? (
                    <Loader2 className="w-4 h-4 shrink-0 animate-spin mt-0.5" />
                  ) : txtVerificationState.status === "verified" ? (
                    <Check className="w-4 h-4 shrink-0 mt-0.5" />
                  ) : txtVerificationState.status === "rate_limited" || txtVerificationState.status === "pending" ? (
                    <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  )}
                  <div>
                    {txtVerificationState.message ||
                      (txtVerificationState.status === "verifying" ? "Querying authoritative DNS..." : "")}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ================= STEP 3: CONNECT DNS ================= */}
          {currentStep === "CONNECT_DNS" && website && (
            <div className="space-y-4" data-testid="step-cname-container">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Route traffic through AIShield.</h3>
                <p className="text-sm text-foreground-muted mt-0.5 leading-relaxed">
                  Point your website’s subdomain to the AIShield ingress address.
                </p>
              </div>

              {cnameLoading ? (
                <div className="p-8 text-center bg-panel-subtle/40 border border-border/80 rounded-[14px] space-y-2.5">
                  <Loader2 className="w-5 h-5 text-brand animate-spin mx-auto" />
                  <div className="text-xs text-foreground-muted">
                    Fetching routing instructions...
                  </div>
                </div>
              ) : cnameError ? (
                <div className="p-3.5 rounded-[12px] bg-verdict-block/10 border border-verdict-block/30 text-verdict-block text-xs flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">Error Loading Instructions</div>
                    <div>{cnameError}</div>
                  </div>
                </div>
              ) : !isIngressConfigured ? (
                /* Unconfigured Ingress Case: Withhold actionable records */
                <div className="p-4 rounded-[14px] bg-amber-500/10 border border-amber-500/30 text-xs space-y-2.5">
                  <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span>AIShield ingress is not configured yet</span>
                  </div>
                  <p className="text-foreground-muted leading-relaxed">
                    The platform administrator has not configured an ingress CNAME target. Actionable routing instructions are withheld until ingress infrastructure is configured.
                  </p>
                  <p className="text-[11px] text-foreground-dim leading-relaxed">
                    Your website ownership remains verified. You can safely save setup and return once ingress routing is provisioned.
                  </p>
                </div>
              ) : cnameInstructions && cnameInstructions.expected_target ? (
                /* Ingress Configured: Show CNAME instructions */
                <>
                  <div className="bg-panel-subtle/40 border border-border/80 rounded-[14px] overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-border/70 bg-panel-subtle/80 flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground">Required DNS CNAME record</span>
                      <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 font-bold">
                        CNAME
                      </span>
                    </div>
                    <div className="p-4 space-y-3 text-xs">
                      {/* Type */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-2.5 border-b border-border/50">
                        <span className="text-foreground-muted font-medium">Type</span>
                        <span className="font-mono font-bold text-blue-400 px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 self-start sm:self-auto">
                          CNAME
                        </span>
                      </div>

                      {/* Name / Host */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2.5 border-b border-border/50">
                        <div className="min-w-0 flex-1">
                          <div className="text-foreground-muted font-medium mb-1">Name / Host</div>
                          <div className="font-mono text-foreground font-medium break-all select-all p-2 rounded-lg bg-panel border border-border/60">
                            {cnameInstructions.record_name}
                          </div>
                        </div>
                        <div className="sm:self-end">
                          <CopyButton value={cnameInstructions.record_name} label="CNAME host" />
                        </div>
                      </div>

                      {/* Target */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-foreground-muted font-medium mb-1">Target</div>
                          <div className="font-mono text-blue-400 font-medium break-all select-all p-2 rounded-lg bg-panel border border-border/60">
                            {cnameInstructions.expected_target}
                          </div>
                        </div>
                        <div className="sm:self-end">
                          <CopyButton value={cnameInstructions.expected_target} label="CNAME target" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Concise Traffic Warning */}
                  <div className="p-3 rounded-[12px] bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200/90 leading-relaxed flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-amber-200">Important:</span> Changing this record redirects visitor traffic through AIShield and should follow the provided setup instructions.
                    </div>
                  </div>

                  {/* Verification Feedback Banner */}
                  {cnameVerificationState.status !== "idle" && (
                    <div
                      role="status"
                      className={`p-3.5 rounded-[12px] text-xs flex items-start gap-2.5 leading-relaxed ${
                        cnameVerificationState.status === "ready"
                          ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
                          : cnameVerificationState.status === "verifying"
                          ? "bg-brand/10 border border-brand/30 text-brand"
                          : cnameVerificationState.status === "rate_limited" || cnameVerificationState.status === "pending"
                          ? "bg-amber-500/10 border border-amber-500/30 text-amber-300"
                          : "bg-verdict-block/10 border border-verdict-block/30 text-verdict-block"
                      }`}
                    >
                      {cnameVerificationState.status === "verifying" ? (
                        <Loader2 className="w-4 h-4 shrink-0 animate-spin mt-0.5" />
                      ) : cnameVerificationState.status === "ready" ? (
                        <Check className="w-4 h-4 shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      )}
                      <div>
                        {cnameVerificationState.message ||
                          (cnameVerificationState.status === "verifying" ? "Testing CNAME delegation..." : "")}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* ================= STEP 4: SETUP STATUS ================= */}
          {currentStep === "SETUP_STATUS" && website && statusSummary && (
            <div className="space-y-4" data-testid="step-status-container">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Connection status.</h3>
                <p className="text-sm text-foreground-muted mt-0.5 leading-relaxed">
                  Review your website’s setup progress.
                </p>
              </div>

              {/* Prominent Website Identity */}
              <div className="p-3.5 rounded-[14px] bg-panel-subtle/60 border border-border/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-foreground truncate">
                    {website.display_name}
                  </div>
                  <div className="text-xs font-mono text-blue-400 truncate mt-0.5">
                    {website.public_hostname}
                  </div>
                </div>
                <div className="text-xs text-foreground-dim font-mono break-all sm:text-right">
                  Origin: <span className="text-foreground-muted">{website.origin_url}</span>
                </div>
              </div>

              {/* Vertical Checklist / Status Summary */}
              <div className="space-y-2">
                {/* 1. Domain ownership */}
                <div className="p-3 rounded-[12px] bg-panel-subtle/40 border border-border/70 flex items-start justify-between gap-3">
                  <div className="space-y-0.5 min-w-0">
                    <div className="text-sm font-medium text-foreground">Domain ownership</div>
                    <div className="text-xs text-foreground-dim leading-relaxed">
                      {statusSummary.ownership.description}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                      statusSummary.ownership.badge === "verified"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                        : statusSummary.ownership.badge === "warning"
                        ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                        : "bg-panel text-foreground-muted border border-border"
                    }`}
                  >
                    {statusSummary.ownership.badge === "verified" ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : statusSummary.ownership.badge === "warning" ? (
                      <AlertTriangle className="w-3.5 h-3.5" />
                    ) : (
                      <Clock className="w-3.5 h-3.5" />
                    )}
                    <span>{statusSummary.ownership.status}</span>
                  </span>
                </div>

                {/* 2. DNS routing */}
                <div className="p-3 rounded-[12px] bg-panel-subtle/40 border border-border/70 flex items-start justify-between gap-3">
                  <div className="space-y-0.5 min-w-0">
                    <div className="text-sm font-medium text-foreground">DNS routing</div>
                    <div className="text-xs text-foreground-dim leading-relaxed">
                      {statusSummary.routing.description}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                      statusSummary.routing.badge === "verified"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                        : statusSummary.routing.badge === "error"
                        ? "bg-verdict-block/10 text-verdict-block border border-verdict-block/30"
                        : statusSummary.routing.badge === "warning"
                        ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                        : "bg-panel text-foreground-muted border border-border"
                    }`}
                  >
                    {statusSummary.routing.badge === "verified" ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : statusSummary.routing.badge === "error" ? (
                      <AlertCircle className="w-3.5 h-3.5" />
                    ) : statusSummary.routing.badge === "warning" ? (
                      <AlertTriangle className="w-3.5 h-3.5" />
                    ) : (
                      <Clock className="w-3.5 h-3.5" />
                    )}
                    <span>{statusSummary.routing.status}</span>
                  </span>
                </div>

                {/* 3. HTTPS certificate */}
                <div className="p-3 rounded-[12px] bg-panel-subtle/40 border border-border/70 flex items-start justify-between gap-3">
                  <div className="space-y-0.5 min-w-0">
                    <div className="text-sm font-medium text-foreground">HTTPS certificate</div>
                    <div className="text-xs text-foreground-dim leading-relaxed">
                      {statusSummary.tls.description}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                      statusSummary.tls.badge === "verified"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                        : statusSummary.tls.badge === "error"
                        ? "bg-verdict-block/10 text-verdict-block border border-verdict-block/30"
                        : "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                    }`}
                  >
                    {statusSummary.tls.badge === "verified" ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : statusSummary.tls.badge === "error" ? (
                      <AlertCircle className="w-3.5 h-3.5" />
                    ) : (
                      <Clock className="w-3.5 h-3.5" />
                    )}
                    <span>{statusSummary.tls.status}</span>
                  </span>
                </div>

                {/* 4. Protection / traffic activation */}
                <div className="p-3 rounded-[12px] bg-panel-subtle/40 border border-border/70 flex items-start justify-between gap-3">
                  <div className="space-y-0.5 min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      Protection / traffic activation
                    </div>
                    <div className="text-xs text-foreground-dim leading-relaxed">
                      {statusSummary.traffic.description}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                      statusSummary.traffic.badge === "verified"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                        : statusSummary.traffic.badge === "error"
                        ? "bg-verdict-block/10 text-verdict-block border border-verdict-block/30"
                        : statusSummary.traffic.badge === "warning"
                        ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                        : "bg-panel text-foreground-muted border border-border"
                    }`}
                  >
                    {statusSummary.traffic.badge === "verified" ? (
                      <ShieldCheck className="w-3.5 h-3.5" />
                    ) : statusSummary.traffic.badge === "error" ? (
                      <AlertCircle className="w-3.5 h-3.5" />
                    ) : statusSummary.traffic.badge === "warning" ? (
                      <AlertTriangle className="w-3.5 h-3.5" />
                    ) : (
                      <Clock className="w-3.5 h-3.5" />
                    )}
                    <span>{statusSummary.traffic.status}</span>
                  </span>
                </div>
              </div>

              {/* Truthful Status Banner */}
              <div
                className={`p-3.5 rounded-[12px] border text-xs leading-relaxed space-y-1 ${
                  statusSummary.banner.type === "active"
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
                    : statusSummary.banner.type === "ready"
                    ? "bg-brand/10 border-brand/30 text-foreground-muted"
                    : statusSummary.banner.type === "error"
                    ? "bg-verdict-block/10 border-verdict-block/30 text-verdict-block"
                    : "bg-amber-500/10 border-amber-500/30 text-amber-200"
                }`}
              >
                <div className="font-semibold text-foreground flex items-center gap-2">
                  {statusSummary.banner.type === "error" ? (
                    <AlertCircle className="w-4 h-4 text-verdict-block shrink-0" />
                  ) : statusSummary.banner.type === "warning" ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                  ) : statusSummary.banner.type === "active" ? (
                    <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-brand shrink-0" />
                  )}
                  <span>{statusSummary.banner.title}</span>
                </div>
                <p className="pl-6">{statusSummary.banner.message}</p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-border/80 flex items-center justify-between shrink-0 bg-panel">
          {/* Step 1 Footer */}
          {currentStep === "DETAILS" && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-12 px-4 rounded-[12px] bg-panel-subtle hover:bg-panel border border-border text-foreground-muted hover:text-foreground font-medium text-sm inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="step-details-form"
                disabled={isSubmittingDetails}
                data-testid="submit-website-details-btn"
                className="h-12 px-5 rounded-[12px] bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-medium text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas shadow-xs"
              >
                {isSubmittingDetails ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating website...</span>
                  </>
                ) : (
                  <>
                    <span>Continue to verification</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </>
          )}

          {/* Step 2 Footer */}
          {currentStep === "VERIFY_TXT" && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-12 px-4 rounded-[12px] bg-panel-subtle hover:bg-panel border border-border text-foreground-muted hover:text-foreground font-medium text-sm inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                Save & resume later
              </button>
              <button
                type="button"
                disabled={txtVerificationState.status === "verifying" || retryAfterCooldown > 0}
                onClick={handleCheckTxtVerification}
                data-testid="check-txt-verification-btn"
                className="h-12 px-5 rounded-[12px] bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-medium text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas shadow-xs"
              >
                {txtVerificationState.status === "verifying" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Checking DNS...</span>
                  </>
                ) : retryAfterCooldown > 0 ? (
                  <>
                    <Clock className="w-4 h-4" />
                    <span>Wait {retryAfterCooldown}s</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Check verification</span>
                  </>
                )}
              </button>
            </>
          )}

          {/* Step 3 Footer */}
          {currentStep === "CONNECT_DNS" && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-12 px-4 rounded-[12px] bg-panel-subtle hover:bg-panel border border-border text-foreground-muted hover:text-foreground font-medium text-sm inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                Save & resume later
              </button>
              {isIngressConfigured ? (
                <button
                  type="button"
                  disabled={cnameVerificationState.status === "verifying" || cnameCooldown > 0}
                  onClick={handleVerifyCname}
                  data-testid="verify-cname-btn"
                  className="h-12 px-5 rounded-[12px] bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-medium text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas shadow-xs"
                >
                  {cnameVerificationState.status === "verifying" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Verifying routing...</span>
                    </>
                  ) : cnameCooldown > 0 ? (
                    <>
                      <Clock className="w-4 h-4" />
                      <span>Wait {cnameCooldown}s</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>Verify DNS routing</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="h-12 px-5 rounded-[12px] bg-panel-subtle hover:bg-panel border border-border text-foreground font-medium text-sm inline-flex items-center justify-center transition-colors"
                >
                  Close
                </button>
              )}
            </>
          )}

          {/* Step 4 Footer */}
          {currentStep === "SETUP_STATUS" && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-12 px-4 rounded-[12px] bg-panel-subtle hover:bg-panel border border-border text-foreground-muted hover:text-foreground font-medium text-sm inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => website && onFinish(website)}
                data-testid="view-dashboard-finish-btn"
                className="h-12 px-5 rounded-[12px] bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-medium text-sm inline-flex items-center justify-center gap-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas shadow-xs"
              >
                <span>Return to dashboard</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};
